import { Size } from '@ps/web-core-store';
import classNames from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { BaseHeader, NavigationTabs, TabView, useWidgetTheme } from '@swf/poker';

import { usePokerSpinAndGoWidget } from '../../../model';
import { WatchGamesTabs } from '../../../types/WatchGames';
import { getAvailableTabs, getInitialSelectedTab } from '../../../utils/watchGames';

import HallOfFame from './HallOfFame/HallOfFame';
import LiveNow from './LiveNow/LiveNow';
import RecentBigWins from './RecentBigWins/RecentBigWins';
import styles from './WatchGames.scss';

interface SearchParams {
  activeTab?: WatchGamesTabs;
  widgetId: string;
  widgetConfigTabs?: WatchGamesTabs[];
}

const WatchGames: React.FC = () => {
  const { activeTab, widgetId, widgetConfigTabs } = useSearchParams<SearchParams>();
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const { className } = useWidgetTheme();
  const { widgetSelect, widgetDispatch } = usePokerSpinAndGoWidget(widgetId);
  const availableTabs = getAvailableTabs(widgetConfigTabs);
  const [selectedTab, setSelectedTab] = useState<WatchGamesTabs>(
    getInitialSelectedTab(activeTab, widgetConfigTabs)
  );
  const watchGames = useSelector(widgetSelect.watchGames);
  const { userImageMap } = watchGames;

  useEffect(() => {
    setStyle({
      closeOnOverlayClick: true,
      closeOnEsc: true,
      overlayClassName: classNames(className, styles.modalOverlay),
      className: classNames(styles.modalContainer, styles.modalContainerCentered),
      size: Size.FLEX
    });
  }, [className, setStyle]);

  useEffect(() => {
    const payloadImageIds = Object.keys(userImageMap).filter(
      (key) => userImageMap[Number(key)] === null && key !== '0'
    );

    if (payloadImageIds.length) {
      widgetDispatch.avatarImageRequest(payloadImageIds);
    }
  }, [widgetDispatch, userImageMap]);

  return (
    <>
      <BaseHeader
        headerClassName={styles.header}
        title={
          <NavigationTabs
            selectedTab={selectedTab}
            onSelect={(tab: WatchGamesTabs) => setSelectedTab(tab)}
            tabTranslations={availableTabs}
          />
        }
      />
      <Body className={styles.body}>
        <TabView activeTab={selectedTab}>
          {availableTabs[WatchGamesTabs.liveNow] && (
            <TabView.Tab name={WatchGamesTabs.liveNow}>
              <LiveNow games={watchGames?.bestLiveNow ?? []} />
            </TabView.Tab>
          )}
          {availableTabs[WatchGamesTabs.hallOfFame] && (
            <TabView.Tab name={WatchGamesTabs.hallOfFame}>
              <HallOfFame games={watchGames?.hallOfFame ?? []} />
            </TabView.Tab>
          )}
          {availableTabs[WatchGamesTabs.recentBigWins] && (
            <TabView.Tab name={WatchGamesTabs.recentBigWins}>
              <RecentBigWins winners={watchGames?.recentBigWins ?? []} />
            </TabView.Tab>
          )}
        </TabView>
      </Body>
    </>
  );
};

export default WatchGames;
